import { Injectable } from '@angular/core'
import { HttpEventType } from '@angular/common/http'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { NotificationType } from 'angular2-notifications'
import { of } from 'rxjs'
import { catchError, delay, filter, map, switchMap, tap } from 'rxjs/operators'
import { AppNotificationService } from '../../services/app-notification.service'
import { AppSpinnerService } from '../../services/app-spinner.service'
import { AppCoreService } from '../../services/app-core.service'
import { WbsRisk } from '../../../features/feature-wbs/models/interfaces/wbs-risk.interface'
import { WbsService } from './services/wbs.service'
import { WbsDeliverableListItem } from '../../../features/feature-wbs/models/interfaces/wbs-deliverable.interface'
import { WbsMilestoneListItem } from '../../../features/feature-wbs/models/interfaces/wbs-milestone.interface'
import { WbsWorkpackageListItem } from '../../../features/feature-wbs/models/interfaces/wbs-workpackage.interface'
import { AppErrorHandlerService } from '../../services/app-error-handler/app-error-handler.service'
import { AppFileDownloadService } from '../../services/app-file-download.service'
import * as WbsActions from './wbs.actions'

@Injectable()
export class WbsEffects {
   exportGantt$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.exportGantt),
         switchMap((action) =>
            this.wbsService.exportGantt().pipe(
               tap((response) => this.fileDownloadService.save(response, 'PMP_Gantt')),
               map((response) => WbsActions.exportGanttSuccess({ response })),
               catchError((error) => of(WbsActions.exportGanttFailed({ error })))
            )
         )
      )
   )

   loadWorkpackageList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.loadWorkpackageList),
         switchMap((action) =>
            this.wbsService.getWorkpackageList().pipe(
               map((response: WbsWorkpackageListItem[]) => WbsActions.loadWorkpackageListSuccess({ response })),
               catchError((error) => of(WbsActions.loadWorkpackageListFailed({ error })))
            )
         )
      )
   )

   exportWorkpackageList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.exportWorkpackageList),
         switchMap((action) =>
            this.wbsService.exportWorkpackageList().pipe(
               tap((response) => this.fileDownloadService.save(response, 'PMP_Wbs_WORKPAGE')),
               map((response) => WbsActions.exportWorkpackageListSuccess({ response })),
               catchError((error) => of(WbsActions.exportWorkpackageListFailed({ error })))
            )
         )
      )
   )

   loadDeliverableList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.loadDeliverableList),
         switchMap((action) =>
            this.wbsService.getDeliverableList().pipe(
               map((response: WbsDeliverableListItem[]) => WbsActions.loadDeliverableListSuccess({ response })),
               catchError((error) => of(WbsActions.loadDeliverableListFailed({ error })))
            )
         )
      )
   )

   exportDeliverableList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.exportDeliverableList),
         switchMap((action) =>
            this.wbsService.exportDeliverableList(action.includeDescription).pipe(
               tap((response) => this.fileDownloadService.save(response, 'PMP_Wbs_DELIVERABLE')),
               map((response) => WbsActions.exportDeliverableListSuccess({ response })),
               catchError((error) => of(WbsActions.exportDeliverableListFailed({ error })))
            )
         )
      )
   )

   loadMilestoneList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.loadMilestoneList),
         switchMap((action) =>
            this.wbsService.getMilestoneList().pipe(
               map((response: WbsMilestoneListItem[]) => WbsActions.loadMilestoneListSuccess({ response })),
               catchError((error) => of(WbsActions.loadMilestoneListFailed({ error })))
            )
         )
      )
   )

   exportMilestoneList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.exportMilestoneList),
         switchMap((action) =>
            this.wbsService.exportMilestoneList().pipe(
               tap((response) => this.fileDownloadService.save(response, 'PMP_Wbs_MILLESTONE')),
               map((response) => WbsActions.exportMilestoneListSuccess({ response })),
               catchError((error) => of(WbsActions.exportMilestoneListFailed({ error })))
            )
         )
      )
   )

   loadRiskList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.loadRiskList),
         switchMap((action) =>
            this.wbsService.getRiskList().pipe(
               map((response: WbsRisk[]) => WbsActions.loadRiskListSuccess({ response })),
               catchError((error) => of(WbsActions.loadRiskListFailed({ error })))
            )
         )
      )
   )

   exportRiskList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.exportRiskList),
         switchMap((action) =>
            this.wbsService.exportRiskList().pipe(
               tap((response) => this.fileDownloadService.save(response, 'PMP_Wbs_RISKS')),
               map((response) => WbsActions.exportRiskListSuccess({ response })),
               catchError((error) => of(WbsActions.exportRiskListFailed({ error })))
            )
         )
      )
   )

   closeExportProgress$ = createEffect(() =>
      this.actions$.pipe(
         ofType(
            WbsActions.exportGanttSuccess,
            WbsActions.exportWorkpackageListSuccess,
            WbsActions.exportDeliverableListSuccess,
            WbsActions.exportMilestoneListSuccess,
            WbsActions.exportRiskListSuccess
         ),
         filter(({ response }) => response.type === HttpEventType.Response),
         delay(1000),
         map(() => WbsActions.closeExportProgress())
      )
   )

   loadRisk$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.loadRisk),
         switchMap((action) =>
            this.wbsService.getRiskById(action.id).pipe(
               map((response: WbsRisk) => WbsActions.loadRiskSuccess({ response })),
               catchError((error) => of(WbsActions.loadRiskFailed({ error })))
            )
         )
      )
   )

   addRisk$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.addRisk),
         switchMap((action) =>
            this.wbsService.insertRisk(action.data).pipe(
               map(() => WbsActions.addRiskSuccess()),
               catchError((error) => of(WbsActions.addRiskFailed({ error })))
            )
         )
      )
   )

   updateRisk$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.updateRisk),
         switchMap((action) =>
            this.wbsService.updateRisk(action.id, action.data).pipe(
               map(() => WbsActions.updateRiskSuccess()),
               catchError((error) => of(WbsActions.updateRiskFailed({ error })))
            )
         )
      )
   )

   deleteRisk$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.deleteRisk),
         switchMap((action) =>
            this.wbsService.deleteRisk(action.id).pipe(
               map(() => WbsActions.deleteRiskSuccess()),
               catchError((error) => of(WbsActions.deleteRiskFailed({ error })))
            )
         )
      )
   )

   updateRiskOrder$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.updateRiskOrder),
         switchMap((action) =>
            this.wbsService.changeRiskOrder(action.data).pipe(
               map(() => WbsActions.updateRiskOrderSuccess()),
               catchError((error) => of(WbsActions.updateRiskOrderFailed({ error })))
            )
         )
      )
   )

   copyRisk$ = createEffect(() =>
      this.actions$.pipe(
         ofType(WbsActions.copyRisk),
         switchMap((action) =>
            this.wbsService.copyRisk(action.id).pipe(
               map(() => WbsActions.copyRiskSuccess()),
               catchError((error) => of(WbsActions.copyRiskFailed({ error })))
            )
         )
      )
   )

   constructor(
      private actions$: Actions,
      private wbsService: WbsService,
      private coreService: AppCoreService,
      private spinnerService: AppSpinnerService,
      private notificationService: AppNotificationService,
      private errorHandlerService: AppErrorHandlerService,
      private fileDownloadService: AppFileDownloadService
   ) {}

   private _actionsForSpinnerHide = [
      WbsActions.updateRiskOrderSuccess,
      WbsActions.addRiskSuccess,
      WbsActions.updateRiskSuccess,
      WbsActions.deleteRiskSuccess,
      WbsActions.copyRiskSuccess,
      WbsActions.deleteRiskFailed,
      WbsActions.updateRiskOrderFailed,
      WbsActions.addRiskFailed,
      WbsActions.updateRiskFailed,
      WbsActions.copyRiskFailed,
      WbsActions.loadRiskListSuccess,
      WbsActions.loadRiskListFailed,
      WbsActions.loadWorkpackageListSuccess,
      WbsActions.loadWorkpackageListFailed,
      WbsActions.loadDeliverableListSuccess,
      WbsActions.loadDeliverableListFailed,
      WbsActions.loadMilestoneListSuccess,
      WbsActions.loadMilestoneListFailed
   ]

   listenAllSpinnerHideAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerHide),
            map(() => this.spinnerService.spinnerHide())
         ),
      { dispatch: false }
   )

   private _actionsForSpinnerShow = [
      WbsActions.addRisk,
      WbsActions.updateRisk,
      WbsActions.deleteRisk,
      WbsActions.copyRisk,
      WbsActions.updateRiskOrder,
      WbsActions.loadRiskList,
      WbsActions.loadWorkpackageList,
      WbsActions.loadDeliverableList,
      WbsActions.loadMilestoneList
   ]

   listenAllSpinnerShowAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerShow),
            map(() => this.spinnerService.spinnerShow())
         ),
      { dispatch: false }
   )

   private _actionsFailedNotification = [
      WbsActions.loadWorkpackageListFailed,
      WbsActions.loadDeliverableListFailed,
      WbsActions.loadMilestoneListFailed,
      WbsActions.loadRiskListFailed,
      WbsActions.deleteRiskFailed,
      WbsActions.updateRiskOrderFailed,
      WbsActions.addRiskFailed,
      WbsActions.updateRiskFailed,
      WbsActions.copyRiskFailed,
      WbsActions.loadRiskFailed,
      WbsActions.exportGanttFailed,
      WbsActions.exportWorkpackageListFailed,
      WbsActions.exportDeliverableListFailed,
      WbsActions.exportMilestoneListFailed,
      WbsActions.exportRiskListFailed
   ]

   listenAllFailedAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsFailedNotification),
            map((errorResponse) => {
               this.errorHandlerService.handleError(errorResponse.error)
            })
         ),
      { dispatch: false }
   )

   private _actionsSuccessNotification = [WbsActions.addRiskSuccess, WbsActions.updateRiskSuccess, WbsActions.deleteRiskSuccess, WbsActions.copyRiskSuccess]

   listenAllSuccessAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsSuccessNotification),
            map(() => {
               this.notificationService.notify(NotificationType.Success)
               this.coreService.closeDialog()
            })
         ),
      { dispatch: false }
   )
}
